import { DEFAULT_LOCALE } from 'dotcom-core/src/constants';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { useLoaderData, useParams } from 'react-router';
import { ROUTE_SEGMENTS } from '~/helpers/app-routes';
import { useIntl } from '~/helpers/intl';
import { type FilterState, buildFilterState } from './buildFilterState';
import type { CatalogLoader } from './route';

const FilterStateContext = createContext<FilterState | undefined>(undefined);

export function FilterStateProvider({ children }: { children: ReactNode }) {
  const { t } = useIntl();
  const { language } = useParams();
  const { slugHierarchyFull, slugMapping, menuFacets, menuRefinement } =
    useLoaderData<CatalogLoader>();
  const allLabel = t('catalog.nav.all', { fallback: 'All' });

  const filterState = useMemo(() => {
    // console.time('buildFilterState');

    const state = buildFilterState({
      allLabel,
      menuFacets,
      menuRefinement,
      slugMapping,
      slugHierarchyFull,
      baseRoute: `/${language ?? DEFAULT_LOCALE}/${ROUTE_SEGMENTS.catalog}`,
    });

    // console.timeEnd('buildFilterState');

    return state;
  }, [
    language,
    allLabel,
    menuFacets,
    menuRefinement,
    slugMapping,
    slugHierarchyFull,
  ]);

  return (
    <FilterStateContext.Provider value={filterState}>
      {children}
    </FilterStateContext.Provider>
  );
}

export function useFilterState() {
  const value = useContext(FilterStateContext);

  if (!value) {
    throw new Error('useFilterState must be used within FilterStateProvider');
  }

  return value;
}
